import { FC, ReactNode } from 'react'
import useTranslation from 'next-translate/useTranslation'
import { FlexboxGrid } from 'rsuite'
import LegacyCheckCircleIcon from '@rsuite/icons/legacy/CheckCircle'
import { useRouter } from 'next/router'

import { useAuth } from '../services/useAuth'
import { formatPriceNumber } from '../utils/math'
import { getSupplierDeliveryDetails } from '../lib/service'
import { getProductCurrency } from '../lib/product'
import { Product, Supplier } from '../utils/types/Product'
import { getCountryAndLocaleStrings } from '../utils/locales'

import styles from '../styles/SupplierOrderInfoSection.module.less'

interface SupplierOrderInfoSectionProps {
  products: Product[]
  supplier: Supplier
}

interface SingleOrderInfoProps {
  className?: string
  children: ReactNode
  testId?: string
}

const SingleOrderInfo: FC<SingleOrderInfoProps> = ({ className = '', testId, children }) => (
  <FlexboxGrid className={`${className} no-flex-wrap`}>
    <FlexboxGrid.Item className="flex-shrink-col-static margin-right-spacer align-middle check-icon-vertical-alignment">
      <LegacyCheckCircleIcon className="icon-check-circle" />
    </FlexboxGrid.Item>
    <FlexboxGrid.Item
      className="flex-shrink-col-dynamic"
      data-testid={testId}
    >
      <p>{children}</p>
    </FlexboxGrid.Item>
  </FlexboxGrid>
)

const SupplierOrderInfoSection: FC<SupplierOrderInfoSectionProps> = (props) => {
  const { products, supplier } = props

  const { t } = useTranslation('products')
  const { locale: countryAndLocale } = useRouter()
  const { isVatIncluded } = useAuth()

  const { country } = getCountryAndLocaleStrings(countryAndLocale)

  const supplierName = supplier['supplier.label'] || ''
  const currency = getProductCurrency(products[0])

  const {
    deliveryCost: supplierShippingFee,
    freeShippingLimit: freeShippingStartsFrom,
    minimumOrderValue: mov,
  } = getSupplierDeliveryDetails(supplier, country, isVatIncluded)

  const hasFullFreeShipping = !supplierShippingFee || !freeShippingStartsFrom

  return (
    <div className={styles['supplier-order-info-section-container']}>
      {!!mov && (
      <SingleOrderInfo testId="min-order-price">
        {`${t('Orders from')} ${formatPriceNumber(mov)} ${currency}`}
      </SingleOrderInfo>
      )}
      {hasFullFreeShipping ? (
        <SingleOrderInfo
          className="flex-flow-row margin-top-spacer"
          testId="free-shipping-price"
        >
          {t('Free delivery for all {{supplier}} products', { supplier: supplierName })}
        </SingleOrderInfo>
      ) : (
        <>
          <SingleOrderInfo
            className="margin-top-spacer"
            testId="shipping-fee"
          >
            {`${t('Supplier shipping fee')} ${formatPriceNumber(supplierShippingFee)} ${currency}`}
          </SingleOrderInfo>
          <SingleOrderInfo
            className="flex-flow-row margin-top-spacer"
            testId="free-shipping-price"
          >
            {`${t('Free shipping over', { currency, price: formatPriceNumber(freeShippingStartsFrom) })}`}
          </SingleOrderInfo>
        </>
      )}
    </div>
  )
}

export default SupplierOrderInfoSection
